import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (userData: {
        email: string;
        password: string;
        first_name: string;
        last_name: string;
        phone?: string;
        company_name?: string;
        account_type: 'professional' | 'individual';
    }) => Promise<void>;
    logout: () => void;
    updateUser: (userData: Partial<User>) => void;
    isAuthenticated: boolean;
    isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Initialize auth state on app load
    useEffect(() => {
        const initAuth = async () => {
            try {
                const storedUser = authService.getStoredUser();
                if (storedUser && authService.isAuthenticated()) {
                    // Verify token is still valid by getting current user
                    const { user: currentUser } = await authService.getCurrentUser();
                    setUser(currentUser);
                }
            } catch (error) {
                // Token is invalid, clear stored data
                authService.logout();
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            const response = await authService.login({ email, password });

            // Ensure we have token and user before storing
            if (response.token && response.user) {
                authService.storeAuthData(response.token, response.user);
                setUser(response.user);
            } else {
                throw new Error('Réponse de connexion invalide');
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Erreur de connexion');
        }
    };

    const register = async (userData: {
        email: string;
        password: string;
        first_name: string;
        last_name: string;
        phone?: string;
        company_name?: string;
        account_type: 'professional' | 'individual';
    }): Promise<void> => {
        try {
            const response = await authService.register(userData);

            // Check if registration requires approval
            if (response.requiresApproval) {
                // Don't store token or set user - they need admin approval first
                throw new Error(response.message);
            } else {
                // Normal registration flow (if approval not required)
                if (response.token && response.user) {
                    authService.storeAuthData(response.token, response.user);
                    setUser(response.user);
                } else {
                    throw new Error('Réponse d\'inscription invalide');
                }
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.error || error.message || 'Erreur lors de la création du compte');
        }
    };

    const logout = (): void => {
        authService.logout();
        setUser(null);
    };

    const updateUser = (userData: Partial<User>): void => {
        if (user) {
            const updatedUser = { ...user, ...userData };
            setUser(updatedUser);
            localStorage.setItem('eldai_user', JSON.stringify(updatedUser));
        }
    };

    const isAuthenticated = !!user;
    const isAdmin = user?.role === 'admin';

    const value: AuthContextType = {
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated,
        isAdmin,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
